import { watch } from "node:fs";
import { access } from "node:fs/promises";
import { homedir } from "node:os";
import { join } from "node:path";
const CODEX_THREAD_WRITER_RECOVERY_RETRY_MS = 30_000;
const CODEX_THREAD_WRITER_MISSING_LOCK_RETRY_MS = 500;
const MIN_MISSING_LOCK_RETRY_MS = 100;
const MAX_MISSING_LOCK_RETRY_MS = 2000;
export async function waitForCodexThreadWriterAvailability(input) {
    if (input.signal?.aborted) {
        return "aborted";
    }
    const codexHome = input.env?.CODEX_HOME?.trim() ||
        process.env.CODEX_HOME?.trim() ||
        join(homedir(), ".codex");
    const lockDirectory = join(codexHome, "thread-writer-locks");
    const lockFileName = `${input.occupancy.agentControllerConversationId}.lock`;
    const lockPath = join(lockDirectory, lockFileName);
    const lockExists = await pathExists(lockPath);
    if (input.signal?.aborted) {
        return "aborted";
    }
    if (!lockExists) {
        return await waitForRetryOrAbort({
            delayMs: normalizeMissingLockRetryMs(input.missingLockRetryMs),
            signal: input.signal,
        });
    }
    return await new Promise((resolve) => {
        let settled = false;
        let recoveryTimer = null;
        let watcher = null;
        const finish = (result) => {
            if (settled) {
                return;
            }
            settled = true;
            if (recoveryTimer) {
                clearTimeout(recoveryTimer);
                recoveryTimer = null;
            }
            watcher?.close();
            input.signal?.removeEventListener("abort", handleAbort);
            resolve(result);
        };
        const handleAbort = () => finish("aborted");
        const observeAvailability = () => {
            pathExists(lockPath)
                .then((exists) => {
                if (!exists) {
                    finish("release_observed");
                }
            })
                .catch(() => {
                // The recovery check below remains the fail-closed backstop.
            });
        };
        input.signal?.addEventListener("abort", handleAbort, { once: true });
        // A crashed writer releases the OS lock without deleting its file.
        // Only official admission can decide ownership; retry_due is not release
        // evidence and must preserve the occupied UI until the provider accepts.
        recoveryTimer = setTimeout(() => finish("retry_due"), input.recoveryRetryMs ?? CODEX_THREAD_WRITER_RECOVERY_RETRY_MS);
        recoveryTimer.unref?.();
        try {
            watcher = watch(lockDirectory, (_eventType, fileName) => {
                if (fileName === null || String(fileName) === lockFileName) {
                    observeAvailability();
                }
            });
            watcher.on("error", () => {
                // Keep waiting for the recovery retry. A watcher error is not proof
                // that the provider released writer ownership.
            });
        }
        catch {
            // Keep waiting for the recovery retry. Missing filesystem observation is
            // not proof that the provider conversation is available.
        }
        observeAvailability();
    });
}
async function waitForRetryOrAbort(input) {
    if (input.signal?.aborted) {
        return "aborted";
    }
    return await new Promise((resolve) => {
        let settled = false;
        let timer = null;
        const finish = (result) => {
            if (settled) {
                return;
            }
            settled = true;
            if (timer) {
                clearTimeout(timer);
                timer = null;
            }
            input.signal?.removeEventListener("abort", handleAbort);
            resolve(result);
        };
        const handleAbort = () => finish("aborted");
        input.signal?.addEventListener("abort", handleAbort, { once: true });
        timer = setTimeout(() => finish("retry_due"), input.delayMs);
        timer.unref?.();
    });
}
function normalizeMissingLockRetryMs(value) {
    if (!(typeof value === "number" && Number.isFinite(value))) {
        return CODEX_THREAD_WRITER_MISSING_LOCK_RETRY_MS;
    }
    return Math.min(MAX_MISSING_LOCK_RETRY_MS, Math.max(MIN_MISSING_LOCK_RETRY_MS, Math.trunc(value)));
}
async function pathExists(path) {
    try {
        await access(path);
        return true;
    }
    catch {
        return false;
    }
}
